import { useCallback, useEffect, useRef } from 'react';

// TTL for the warm Rewards iframe cache. A backstop for the case where the tab stays focused
// the whole time (the focus-loss and account-switch guards below cover everything else), so it
// can be generous; reload re-arms Bing's ANID guard when it expires. Easy to tune.
const REWARDS_CACHE_TTL_MS = 300000;

export interface RewardsCacheSafeWindow {
    // True only when the cached iframe can be safely revealed without a reload: the tab never
    // lost focus/visibility since it was last shown, the active OWA account still matches the
    // one the iframe was loaded for, AND the cache is within its TTL. Otherwise the caller must
    // force a reload so Bing re-checks the ANID. Pass the current coprincipal account key so an
    // in-tab account switch (which fires no focus/visibility event) invalidates the cache.
    isCacheTrusted: (currentIdentityKey: string) => boolean;
    // Call whenever the iframe has (re)loaded / been shown, to (re)arm the safe window for the
    // given coprincipal account key.
    markLoaded: (identityKey: string) => void;
}

/**
 * TEMPORARY OWA-only workaround (Rewards panel caching).
 *
 * The Bing Rewards flyout re-checks OWA's ANID against the live bing.com cookie on every
 * load. Caching the iframe skips that re-check, so we only trust the warm cache inside a
 * "safe window": no tab focus/visibility loss since the panel was last shown (a cross-tab
 * Bing account swap requires leaving the OWA tab), the active OWA account is unchanged (an
 * in-tab account switch fires no focus/visibility event but changes the ANID), AND within the
 * TTL. Any focus loss, account change, or TTL expiry marks the cache untrusted, and the caller
 * forces a reload that re-arms the Bing-side identity guard the next time the panel opens.
 */
export function useRewardsCacheSafeWindow(): RewardsCacheSafeWindow {
    // Set once the OWA tab loses focus/visibility; a cookie swap requires leaving the tab,
    // so a lost-focus event means the cache can no longer be trusted.
    const isDirty = useRef<boolean>(true);
    // performance.now() timestamp of the last (re)load; monotonic, ideal for a TTL.
    const lastLoadedAt = useRef<number>(0);
    // Coprincipal account key the warm iframe was loaded for. An in-tab OWA account switch
    // changes the active mailbox (and its ANID) without any focus/visibility event, so we
    // compare this on reopen and force a reload when it differs — otherwise the previous
    // account's Rewards could be revealed under the new account (bug #448861 identity guard).
    const loadedIdentityKey = useRef<string | undefined>(undefined);

    useEffect(() => {
        const markDirty = () => {
            isDirty.current = true;
        };
        const onVisibilityChange = () => {
            if (document.hidden) {
                markDirty();
            }
        };
        window.addEventListener('blur', markDirty);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('blur', markDirty);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    const markLoaded = useCallback((identityKey: string) => {
        // Don't re-arm while the OWA tab is backgrounded or unfocused: if the iframe finished
        // loading while hidden, a Bing cookie swap could still happen before the user returns.
        // Leaving the window dirty forces a safe reload on the next open.
        if (document.hidden || (typeof document.hasFocus === 'function' && !document.hasFocus())) {
            isDirty.current = true;
            return;
        }
        lastLoadedAt.current = performance.now();
        loadedIdentityKey.current = identityKey;
        isDirty.current = false;
    }, []);

    const isCacheTrusted = useCallback((currentIdentityKey: string) => {
        if (isDirty.current) {
            return false;
        }
        // Force a reload if the active OWA account changed since the iframe was loaded.
        if (loadedIdentityKey.current !== currentIdentityKey) {
            return false;
        }
        return performance.now() - lastLoadedAt.current <= REWARDS_CACHE_TTL_MS;
    }, []);

    return { isCacheTrusted, markLoaded };
}

export default useRewardsCacheSafeWindow;
